import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, Select, MenuItem, TextField } from '@mui/material';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../css/machines/AddMachineDialoge.css';

export const AddMachineDialoge = ({ openAddMachine, setOpenAddMachine, setOpenSuccessfulAddMachine, setMachineId, machines, setMachines, totalMachines, setTotalMachines, filteredMachinesCount, setFilteredMachinesCount }) => {

    const { t } = useTranslation();
    const authenticatedUser = useSelector(state => state.AuthenticatedUser);

    const [regions, setRegions] = useState([]);
    const [sites, setSites] = useState([]);
    const [poles, setPoles] = useState([]);

    const formik = useFormik({
        initialValues: {
            machineName: '',
            selectedRegion: authenticatedUser?.user_region_ids?.length === 1 ? authenticatedUser?.user_region_ids[0] : '',
            selectedSite: '',
            selectedPole: '',
        },
        validationSchema: Yup.object().shape({
            machineName: Yup.string().required(t('isRequired')),
            selectedRegion: Yup.string().required(t('isRequired')),
            selectedSite: Yup.string().required(t('isRequired')),
            selectedPole: Yup.string().required(t('isRequired')),
        }),
        onSubmit: async (values) => {
            try {
                const machineData = {
                    name: values.machineName,
                    region_id: values.selectedRegion,
                    site_id: values.selectedSite,
                    pole_id: values.selectedPole,
                };

                const response = await axios.post('machine', { machineData });
                if (response.status === 200 || response.status === 201) {
                    setMachineId(response.data.id);
                    setMachines([...machines, response.data]);
                    setTotalMachines(totalMachines + 1);
                    setFilteredMachinesCount(filteredMachinesCount + 1)
                    setOpenAddMachine(false);
                    setOpenSuccessfulAddMachine(true);
                }
            } catch (error) {
                console.error('Error adding machine:', error);
                CustomErrorLogger(error, "error adding machine in AddMachineDialoge");
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setOpenAddMachine(false);
    }, [setOpenAddMachine]);

    const fetchRegions = useCallback(async () => {
        try {
            const response = await axios.get('region');
            if (response.status === 200 || response.status === 201) {
                setRegions(response.data);
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            CustomErrorLogger(error, "error fetching regions in AddMachineDialoge");
        }
    }, []);

    const fetchSites = useCallback(async () => {
        if (values?.selectedRegion?.length === 0) return;
        try {
            const response = await axios.get('site', { params: { regions: [values.selectedRegion] } });
            if (response.status === 200 || response.status === 201) {
                setSites(response.data);
            }
        } catch (error) {
            console.error('Error fetching sites:', error);
            CustomErrorLogger(error, "error fetching sites in AddMachineDialoge");
        }
    }, [values.selectedRegion]);

    const fetchPoles = useCallback(async () => {
        if (values?.selectedSite?.length === 0) {
            setPoles([]);
            return;
        };
        try {
            const response = await axios.get('pole', { params: { sites: [values.selectedSite] } });
            if (response.status === 200 || response.status === 201) {
                setPoles(response.data);
            }
        } catch (error) {
            console.error('Error fetching poles:', error);
            CustomErrorLogger(error, "error fetching poles in AddMachineDialoge");
        }
    }, [values.selectedSite]);

    return (
        <Dialog open={openAddMachine} maxWidth='md'>
            <IconButton onClick={handleClose} className='addMachine_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addMachine_close_icon' />
            </IconButton>
            <DialogTitle className='addMachine_dialog_title'>{t('addMachineDialogeTitle')}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent className='addMachine_dialog_content'>
                    <Typography className='addMachine_dialog_SubTitle'>{t('addMachineDialogeSubTitle')}</Typography>
                    <Box className="addMachine_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addMachineInputTitle">{t('addMachineInputLabel')}</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.machineName}
                            className='addMachine_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="machineName"
                            error={touched.machineName && Boolean(errors.machineName)}
                            helperText={touched.machineName && errors.machineName}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    {authenticatedUser.user_region_ids.length > 1 &&
                        <Box className='addMachine_helper_selct_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addMachineInputTitle">{t('Region')}</FormHelperText>
                            <Select
                                className={`addMachine_select_metier`}
                                value={values.selectedRegion}
                                onChange={(e) => {
                                    handleChange(e);
                                    setFieldValue('selectedSite', '');
                                    setFieldValue('selectedPole', '');
                                }}
                                onBlur={handleBlur}
                                onOpen={fetchRegions}
                                name="selectedRegion"
                                error={touched.selectedRegion && Boolean(errors.selectedRegion)}
                            >
                                {regions?.map((region) => (
                                    <MenuItem
                                        key={region.id} className='addMachine_select_metier_item' value={region.id}>
                                        {region.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {touched.selectedRegion && errors.selectedRegion && (
                                <FormHelperText error className='addMachine_select_helper'>{errors.selectedRegion}</FormHelperText>
                            )}
                        </Box>
                    }
                    <Box className='addMachine_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addMachineInputTitle">{t('Site')}</FormHelperText>
                        <Select
                            className={`addMachine_select_metier`}
                            value={values.selectedSite}
                            onChange={(e) => {
                                handleChange(e);
                                setFieldValue('selectedPole', '');
                            }}
                            onBlur={handleBlur}
                            onOpen={fetchSites}
                            name="selectedSite"
                            error={touched.selectedSite && Boolean(errors.selectedSite)}
                        >
                            {sites?.map((site) => (
                                <MenuItem
                                    key={site.id} className='addMachine_select_metier_item' value={site.id}>
                                    {site.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {touched.selectedSite && errors.selectedSite && (
                            <FormHelperText error className='addMachine_select_helper'>{errors.selectedSite}</FormHelperText>
                        )}
                    </Box>
                    <Box className='addMachine_helper_selct_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addMachineInputTitle">{t('Pôle')}</FormHelperText>
                        <Select
                            className={`addMachine_select_metier`}
                            value={values.selectedPole}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onOpen={fetchPoles}
                            name="selectedPole"
                            error={touched.selectedPole && Boolean(errors.selectedPole)}
                        >
                            {poles?.map(pole => (
                                <MenuItem key={pole.id} className='addMachine_select_metier_item' value={pole.id}>
                                    {pole.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {touched.selectedPole && errors.selectedPole && (
                            <FormHelperText error className='addMachine_select_helper'>{errors.selectedPole}</FormHelperText>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions className='addMachine_dialog_actions'>
                    <Button className='addMachine_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addMachine_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};